import { useState, useEffect } from 'react';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Plus, Edit, Trash2, Search, ChevronLeft, ChevronRight, Truck, Phone, Mail, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';

const ProveedoresCRUD = ({ canCreate = true, canUpdate = true, canDelete = true }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [search, setSearch] = useState('');
  
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ 
    proveedor: '', description: '', telefono: '', email: '' 
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);

  // Sorting state
  const [sortField, setSortField] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const sortedItems = [...items].sort((a, b) => {
    const aVal = (a[sortField] || '').toString().toLowerCase();
    const bVal = (b[sortField] || '').toString().toLowerCase();
    return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });

  const handleSort = (field) => {
    if (sortField === field) setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('asc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    return sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" />;
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pagination.page, limit: pagination.limit });
      if (search) params.append('search', search);
      
      const response = await api.get(`/config/proveedores?${params}`);
      setItems(response.data.data);
      setPagination(prev => ({ ...prev, total: response.data.total, pages: response.data.pages }));
    } catch (error) {
      toast.error('Error al cargar proveedores');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchItems(); }, [pagination.page]);
  
  useEffect(() => {
    const t = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchItems();
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({ proveedor: '', description: '', telefono: '', email: '' });
    setModalOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      proveedor: item.name || '',
      description: item.description || '',
      telefono: item.telefono || '',
      email: item.email || ''
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.proveedor.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    setSaving(true);
    try {
      if (editingItem) {
        await api.put(`/config/proveedores/${editingItem.id}`, formData);
        toast.success('Proveedor actualizado exitosamente');
      } else {
        await api.post('/config/proveedores', formData);
        toast.success('Proveedor creado exitosamente');
      }
      setModalOpen(false);
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    try {
      await api.delete(`/config/proveedores/${item.id}`);
      toast.success('Proveedor eliminado exitosamente');
      setDeleteConfirm(null);
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Truck className="h-5 w-5 text-primary" strokeWidth={1.5} />
          <div>
            <h3 className="font-semibold text-foreground">Proveedores</h3>
            <p className="text-xs text-muted-foreground">{pagination.total} registros</p>
          </div>
        </div>
        <Button
          size="sm"
          className={`btn btn-primary h-8 ${!canCreate ? 'opacity-50 cursor-not-allowed' : ''}`}
          onClick={canCreate ? handleCreate : undefined}
          disabled={!canCreate}
          data-testid="add-proveedor-btn"
        >
          <Plus className="h-3.5 w-3.5" />
          Nuevo
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por nombre, email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 h-9 bg-background"
        />
      </div>

      <Card className="card-subtle overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent"></div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="table-subtle text-sm">
                  <thead>
                    <tr>
                      <th className="cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('name')}>
                        <div className="flex items-center gap-1.5">NOMBRE <SortIcon field="name" /></div>
                      </th>
                      <th className="cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('description')}>
                        <div className="flex items-center gap-1.5">DESCRIPCION <SortIcon field="description" /></div>
                      </th>
                      <th className="cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('telefono')}>
                        <div className="flex items-center gap-1.5">TELEFONO <SortIcon field="telefono" /></div>
                      </th>
                      <th className="cursor-pointer hover:text-primary transition-colors select-none" onClick={() => handleSort('email')}>
                        <div className="flex items-center gap-1.5">EMAIL <SortIcon field="email" /></div>
                      </th>
                      <th className="w-20 text-right">ACCIONES</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems.length === 0 ? (
                      <tr>
                        <td colSpan={5}>
                          <div className="text-center py-8 text-muted-foreground">
                            No se encontraron proveedores
                          </div>
                        </td>
                      </tr>
                    ) : sortedItems.map((item) => (
                      <tr key={item.id}>
                        <td className="font-medium text-primary">{item.name}</td>
                        <td className="text-muted-foreground max-w-[200px] truncate">{item.description || '-'}</td>
                        <td className="text-muted-foreground">
                          {item.telefono && item.telefono !== 'SIN ESPECIFICAR' ? (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />{item.telefono}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="text-muted-foreground">
                          {item.email && item.email !== 'SIN ESPECIFICAR' ? (
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />{item.email}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="w-20 text-right">
                          <div className="flex justify-end gap-0.5">
                            <button
                              onClick={() => canUpdate && handleEdit(item)}
                              className={`action-btn action-btn-edit ${!canUpdate ? 'opacity-50' : ''}`}
                              disabled={!canUpdate}
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => canDelete && setDeleteConfirm(item)}
                              className={`action-btn-delete ${!canDelete ? 'opacity-50' : ''}`}
                              disabled={!canDelete}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {pagination.pages > 1 && (
                <div className="flex items-center justify-between px-4 py-2 border-t border-border/30">
                  <p className="text-xs text-muted-foreground">Pág {pagination.page} de {pagination.pages}</p>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} disabled={pagination.page === 1} className="h-7 w-7">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} disabled={pagination.page >= pagination.pages} className="h-7 w-7">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="modal-content max-w-lg">
          <DialogHeader className="modal-header">
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-primary" />
              {editingItem ? 'Editar Proveedor' : 'Nuevo Proveedor'}
            </DialogTitle>
          </DialogHeader>
          <div className="modal-body py-4 grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="text-sm font-medium">Nombre *</Label>
              <Input
                value={formData.proveedor}
                onChange={(e) => setFormData(prev => ({ ...prev, proveedor: e.target.value }))}
                className="input-subtle mt-1.5"
                placeholder="Nombre del proveedor"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-sm font-medium">Descripcion</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="input-subtle mt-1.5"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Telefono</Label>
              <Input
                value={formData.telefono}
                onChange={(e) => setFormData(prev => ({ ...prev, telefono: e.target.value }))}
                className="input-subtle mt-1.5"
                placeholder="+54 11 1234-5678"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Email</Label>
              <Input
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="input-subtle mt-1.5"
                placeholder="contacto@proveedor.com"
              />
            </div>
          </div>
          <DialogFooter className="modal-footer">
            <Button variant="outline" onClick={() => setModalOpen(false)} disabled={saving}>Cancelar</Button>
            <Button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="modal-content">
          <DialogHeader className="modal-header">
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Confirmar Eliminacion
            </DialogTitle>
          </DialogHeader>
          <div className="modal-body py-4">
            <p className="text-muted-foreground">
              Desea eliminar el proveedor <span className="font-semibold text-foreground">{deleteConfirm?.name}</span>?
            </p>
          </div>
          <DialogFooter className="modal-footer">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
            <Button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm)}>Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProveedoresCRUD;
